import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  type ChartSeriesItemProps,
  ChartValueAxis,
  ChartValueAxisItem,
  type ChartValueAxisItemProps,
} from '@progress/kendo-react-charts'
import { useAtomValue } from 'jotai'
import React from 'react'
import * as R from 'remeda'
import { match } from 'ts-pattern'

import { isChartMaxUnifiedAtom } from '../../atoms'
import { useMinMax } from '../../hooks/useMinMax'
import { getAppropriateChartInfo, type SeriesItem } from '../../utils'

import styles from './TimeSeriesChart.css'

export type TimeSeriesChartProps = {
  categories: string[]
  chartType: Extract<ChartSeriesItemProps['type'], 'column' | 'line'>
  isFixedWidth?: boolean
  isPercentage?: boolean
  series: SeriesItem[]
}

export const TimeSeriesChart = ({
  categories,
  chartType,
  isFixedWidth = false,
  isPercentage = false,
  series,
}: TimeSeriesChartProps) => {
  const rootStyle = React.useMemo<React.CSSProperties>(
    () => ({
      height: 200 + 22 * (series.length - 1),
      width: isFixedWidth ? 400 : undefined,
    }),
    [series.length, isFixedWidth],
  )

  const format = React.useMemo(
    () => (isPercentage ? 'p1' : 'n'),
    [isPercentage],
  )

  const isChartMaxUnified = useAtomValue(isChartMaxUnifiedAtom)
  const { max, min } = useMinMax()
  const valueAxisItemProps = React.useMemo<ChartValueAxisItemProps>(() => {
    const props = match([isChartMaxUnified, isPercentage])
      .with([true, true], () => ({ max: 1, min: 0 }))
      .with([true, false], () => ({ max, min }))
      .with([false, true], () => ({}))
      .with([false, false], () => {
        const values = series.flatMap((item) => item.data)
        const chartInfo = getAppropriateChartInfo(values, { min })

        return {
          ...R.pick(chartInfo, ['max', 'min']),
          majorUnit: chartInfo.step,
        }
      })
      .exhaustive()

    return { labels: { format }, ...props }
  }, [format, isChartMaxUnified, isPercentage, max, min, series])

  const font = React.useMemo(() => {
    const bodyStyle = window.getComputedStyle(window.document.body)

    return `10px ${bodyStyle.fontFamily}`
  }, [])

  const chartSeriesItemLabels: ChartSeriesItemProps['labels'] = React.useMemo(
    () => ({ font, format }),
    [font, format],
  )

  return (
    <div style={rootStyle}>
      <Chart className={styles.chart} transitions={false}>
        <ChartLegend
          item={{ highlight: { visible: false }, markers: { visible: false } }}
          orientation="horizontal"
          position="top"
        />

        <ChartValueAxis>
          <ChartValueAxisItem {...valueAxisItemProps} />
        </ChartValueAxis>

        <ChartCategoryAxis>
          <ChartCategoryAxisItem
            categories={categories}
            labels={{ font, rotation: 'auto' }}
          />
        </ChartCategoryAxis>

        <ChartSeries>
          {series.map((item, index) => (
            <ChartSeriesItem
              key={index}
              data={item.data}
              labels={{
                ...chartSeriesItemLabels,
                visible: item.isLabelVisible,
              }}
              name={item.name}
              type={chartType}
            />
          ))}
        </ChartSeries>
      </Chart>
    </div>
  )
}
